"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { searchAPI } from "../../services/api";
import { usePostModal } from "../../contexts/PostModalContext";
import { useAuth } from "../../hooks/useAuth";
import { getUserTypeColor } from "../../utils/userTypeUtils";
import { getUserPlaceholderImage } from "../../utils/imageUtils";
import "./SearchPage.css";

// Accent scheme driven by the logged-in user's type (same rule as the navbar)
const getColorScheme = (userType) => {
  const type = userType?.toUpperCase();
  switch (type) {
    case "VETERINARIAN":
    case "VET":
      return { primary: "#00D4B3", hover: "#00C4A7", light: "#F0FFF4", secondary: "#E0FBDE" };
    case "SERVICE_PROVIDER":
    case "BUSINESS":
      return { primary: "#D65745", hover: "#C7493A", light: "#FFF5F5", secondary: "#FEEBE7" };
    case "FIRM":
      return { primary: "#1B3A75", hover: "#162E5E", light: "#F0F4FC", secondary: "#E8EEF8" };
    case "PET_GUARDIAN":
    default:
      return { primary: "#8548A8", hover: "#7A4397", light: "#FAF5FF", secondary: "#F3E8FF" };
  }
};

const TABS = [
  { key: "all", labelKey: "search.all" },
  { key: "people", labelKey: "search.people" },
  { key: "posts", labelKey: "search.posts" },
  { key: "communities", labelKey: "search.communities" },
  { key: "clinics", labelKey: "search.clinics" },
];

const PEOPLE_SUBMENU = [
  { value: null, labelKey: "search.all_types", fallback: "All types" },
  {
    value: "PET_GUARDIAN",
    labelKey: "search.type_pet_guardian",
    fallback: "Pet Guardian",
    children: [
      { value: "parent", labelKey: "search.subtype_parent", fallback: "Parent" },
      { value: "influencer", labelKey: "search.subtype_influencer", fallback: "Influencer" },
      { value: "rescuer", labelKey: "search.subtype_rescuer", fallback: "Rescuer" },
    ],
  },
  {
    value: "VETERINARIAN",
    labelKey: "search.type_veterinarian",
    fallback: "Veterinarian",
    children: [
      { value: "clinic_owner", labelKey: "search.subtype_clinic_owner", fallback: "Clinic Owner" },
      { value: "clinic_member", labelKey: "search.subtype_clinic_member", fallback: "Clinic Member" },
      { value: "freelancer", labelKey: "search.subtype_freelancer", fallback: "Freelancer" },
    ],
  },
  { value: "SERVICE_PROVIDER", labelKey: "search.type_service_provider", fallback: "Service Provider" },
  {
    value: "FIRM",
    labelKey: "search.type_firm",
    fallback: "Firm",
    children: [
      { value: "government", labelKey: "search.subtype_government", fallback: "Government" },
      { value: "associate", labelKey: "search.subtype_associate", fallback: "Associate" },
      { value: "sponsor", labelKey: "search.subtype_sponsor", fallback: "Sponsor" },
    ],
  },
];

const POSTS_SUBMENU = [
  { value: null, labelKey: "search.all_posts", fallback: "All posts" },
  { value: "adoption", labelKey: "search.adoption_posts", fallback: "Adoption posts" },
  {
    value: "normal",
    labelKey: "search.normal_posts",
    fallback: "Normal posts",
    children: [
      { value: "post", labelKey: "search.posttype_post", fallback: "Posts" },
      { value: "poll", labelKey: "search.posttype_poll", fallback: "Polls" },
      { value: "question", labelKey: "search.posttype_question", fallback: "Questions" },
    ],
  },
];

const PREVIEW_LIMIT = 5;

const PeopleIcon = () => (
  <svg className="search-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const PostsIcon = () => (
  <svg className="search-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const CommunitiesIcon = () => (
  <svg className="search-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M8 14s1.5 2 4 2 4-2 4-2" />
    <line x1="9" y1="9" x2="9.01" y2="9" />
    <line x1="15" y1="9" x2="15.01" y2="9" />
  </svg>
);

const ClinicsIcon = () => (
  <svg className="search-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21h18" />
    <path d="M5 21V7l7-4 7 4v14" />
    <path d="M12 9v6" />
    <path d="M9 12h6" />
  </svg>
);

const SearchIcon = () => (
  <svg className="search-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const PeopleRow = ({ person, onOpen }) => {
  const userTypeColor = getUserTypeColor(person.user_type);

  return (
    <button type="button" className="search-result-row" onClick={() => onOpen(person)}>
      <img
        src={person.profile_image_url || getUserPlaceholderImage()}
        alt={person.full_name || person.user_name || "User"}
        className="search-result-avatar"
        style={{ borderColor: userTypeColor }}
        onError={(e) => {
          e.target.src = getUserPlaceholderImage();
        }}
      />
      <div className="search-result-row-body">
        <div className="search-result-row-title">{person.full_name || person.user_name || "Unknown user"}</div>
        <div className="search-result-row-subtitle">
          {person.user_type ? String(person.user_type).replace(/_/g, " ").toLowerCase() : ""}
        </div>
      </div>
      <span className="search-result-badge" style={{ backgroundColor: `${userTypeColor}1A`, color: userTypeColor }}>
        {String(person.user_type || "USER").replace(/_/g, " ")}
      </span>
    </button>
  );
};

const PostRow = ({ post, onOpen }) => {
  const authorName = post.author?.name || post.user?.full_name
    || `${post.user?.first_name || ""} ${post.user?.last_name || ""}`.trim()
    || "Unknown user";
  const authorAvatar = post.author?.avatar || post.user?.image || getUserPlaceholderImage(post.user?.gender);
  const authorTypeColor = getUserTypeColor(post.user?.user_type);
  const media = post.media && post.media.length > 0 ? post.media[0] : null;
  const content = post.excerpt || post.content || "";

  return (
    <button type="button" className="search-result-row" onClick={() => onOpen(post)}>
      {media ? (
        <img src={media.url} alt="" className="search-result-thumb" onError={(e) => { e.target.style.display = "none"; }} />
      ) : null}
      <div className="search-result-row-body">
        <div className="search-result-row-title search-result-row-text">{content}</div>
        <div className="search-result-row-subtitle">
          <span className="search-result-author" style={{ color: authorTypeColor }}>{authorName}</span>
          {post.channel?.name ? ` · ${post.channel.name}` : ""}
          {typeof post.reactions_count === "number" ? ` · ${post.reactions_count} reactions` : ""}
          {typeof post.comments_count === "number" ? ` · ${post.comments_count} comments` : ""}
        </div>
      </div>
      <img
        src={authorAvatar}
        alt=""
        className="search-result-avatar search-result-avatar-sm"
        style={{ borderColor: authorTypeColor }}
        onError={(e) => { e.target.src = getUserPlaceholderImage(); }}
      />
    </button>
  );
};

const CommunityRow = ({ community, onOpen }) => {
  const ownerTypeColor = getUserTypeColor(community.owner?.user_type);

  return (
    <button type="button" className="search-result-row" onClick={() => onOpen(community)}>
      <img
        src={community.profile_image || "/images/social/pic-placeholder.png"}
        alt={community.name || "Community"}
        className="search-result-avatar"
        style={{ borderColor: ownerTypeColor }}
        onError={(e) => {
          e.target.src = "/images/social/pic-placeholder.png";
        }}
      />
      <div className="search-result-row-body">
        <div className="search-result-row-title">{community.name || "Unnamed community"}</div>
        <div className="search-result-row-subtitle">
          {typeof community.members_count === "number" ? `${community.members_count} member${community.members_count === 1 ? "" : "s"}` : "Community"}
          {community.privacy_setting ? ` · ${community.privacy_setting}` : ""}
        </div>
      </div>
    </button>
  );
};

const ClinicRow = ({ clinic, onOpen }) => {
  return (
    <button type="button" className="search-result-row" onClick={() => onOpen(clinic)}>
      <img
        src={clinic.logo || getUserPlaceholderImage()}
        alt={clinic.name_en || "Clinic"}
        className="search-result-avatar"
        style={{ borderColor: "#00D4B3", borderRadius: "10px" }}
        onError={(e) => {
          e.target.src = getUserPlaceholderImage();
        }}
      />
      <div className="search-result-row-body">
        <div className="search-result-row-title">{clinic.name_en || clinic.name_ar || "Unnamed clinic"}</div>
        <div className="search-result-row-subtitle">
          {clinic.address || [clinic.city, clinic.country].filter(Boolean).join(", ")}
        </div>
      </div>
      {typeof clinic.average_rating === "number" && clinic.average_rating > 0 ? (
        <span className="search-result-badge" style={{ backgroundColor: "#F0FDFA", color: "#00857F" }}>
          <span aria-hidden="true">&#9733;</span> {Number(clinic.average_rating).toFixed(1)}
        </span>
      ) : null}
    </button>
  );
};

const Section = ({ icon, title, count, onSeeAll, children }) => (
  <section className="search-section">
    <header className="search-section-header">
      <div className="search-section-heading">
        {icon}
        <h2 className="search-section-title">{title}</h2>
        <span className="search-section-count">{count}</span>
      </div>
      {typeof onSeeAll === "function" ? (
        <SeeAllButton onClick={onSeeAll} />
      ) : null}
    </header>
    <div className="search-section-body">{children}</div>
  </section>
);

const SeeAllButton = ({ onClick }) => {
  const tr = useTr();
  return (
    <button type="button" className="search-see-all" onClick={onClick}>
      {tr("search.see_all", "See all")} <span aria-hidden="true">&rarr;</span>
    </button>
  );
};

const useTr = () => {
  const { t } = useTranslation();
  return useCallback((key, fallback, params) => {
    const value = t(key, params);
    if (value !== key) return value;
    return fallback.replace("{query}", params?.query ?? "");
  }, [t]);
};

const SubMenu = ({ options, current, childCurrent, onSelect, onSelectChild }) => {
  const tr = useTr();
  return (
    <div className="search-subtabs">
      {options.map((opt) => {
        // Keep children visible while one of them is selected, not only
        // while the parent option itself is active.
        const childrenExpanded = Boolean(
          opt.children && (current === opt.value || opt.children.some((c) => c.value === current))
        );
        return (
          <React.Fragment key={opt.value ?? "all"}>
            <button
              type="button"
              className={`search-subtab${(current ?? null) === opt.value ? " search-subtab-active" : ""}`}
              onClick={() => onSelect(opt.value)}
            >
              {tr(opt.labelKey, opt.fallback)}
            </button>
            {childrenExpanded ? (
              <div className="search-subtabs search-subtabs-nested">
                {opt.children.map((child) => (
                  <button
                    key={child.value}
                    type="button"
                    className={`search-subtab${(childCurrent ?? current ?? null) === child.value ? " search-subtab-active" : ""}`}
                    onClick={() => onSelectChild(child.value)}
                  >
                    {tr(child.labelKey, child.fallback)}
                  </button>
                ))}
              </div>
            ) : null}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const EmptyState = ({ query, tkey, fallback }) => {
  const tr = useTr();
  return (
    <div className="search-empty-state">
      <SearchIcon />
      <p className="search-empty-title">{tr(tkey, fallback, { query })}</p>
      <p className="search-empty-subtitle">{tr("search.empty_hint", "Check the spelling or try different keywords.")}</p>
    </div>
  );
};

const QuerySearchBox = ({ query, searchParams, setSearchParams }) => {
  const tr = useTr();
  const [value, setValue] = useState(query);

  useEffect(() => {
    setValue(query);
  }, [query]);

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === query) return;
    const params = new URLSearchParams(searchParams);
    params.set("q", trimmed);
    setSearchParams(params);
  };

  return (
    <div className="search-page-query-box">
      <svg className="search-page-query-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
      <input
        type="text"
        className="search-page-query-input"
        value={value}
        placeholder={tr("search.input_placeholder", "Search Sulhafa...")}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
        }}
      />
    </div>
  );
};

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { openPostModal } = usePostModal();
  const { user, getUserType } = useAuth();
  const tr = useTr();

  const colors = getColorScheme(getUserType?.() || user?.user_type);
  const TAB_FALLBACKS = { all: "All", people: "People", posts: "Posts", communities: "Communities", clinics: "Clinics" };
  const SECTION_TITLES = {
    people: tr("search.people", "People"),
    posts: tr("search.posts", "Posts"),
    communities: tr("search.communities", "Communities"),
    clinics: tr("search.clinics", "Clinics"),
  };
  const SECTION_ICONS = {
    people: <PeopleIcon />,
    posts: <PostsIcon />,
    communities: <CommunitiesIcon />,
    clinics: <ClinicsIcon />,
  };

  const query = (searchParams.get("q") || "").trim();
  const type = searchParams.get("type") || "all";
  const userType = searchParams.get("user_type");
  const subType = searchParams.get("sub_type");
  const postType = searchParams.get("post_type");
  const filterActive = Boolean(
    (type === "people" && (userType || subType)) || (type === "posts" && postType)
  );

  const [allData, setAllData] = useState(null);
  const [counts, setCounts] = useState({ people: 0, posts: 0, communities: 0, clinics: 0 });
  const [items, setItems] = useState({ people: [], posts: [], communities: [], clinics: [] });
  const [pagination, setPagination] = useState(null);
  const [filteredTotal, setFilteredTotal] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const requestSeq = useRef(0);

  const switchTab = (nextType) => {
    const params = new URLSearchParams(searchParams);
    params.set("q", query);
    if (nextType === "all") {
      params.delete("type");
    } else {
      params.set("type", nextType);
    }
    setSearchParams(params);
  };

  const setFilterParam = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params);
  };

  const openPerson = useCallback((person) => {
    navigate(`/profile/${person.user_id || person.id}`);
  }, [navigate]);

  // Open the post in the app's shared post modal — stay on the search page
  const openPost = useCallback((post) => {
    if (post?.id) {
      openPostModal(post.id);
    }
  }, [openPostModal]);

  const openCommunity = useCallback((community) => {
    navigate(`/social/communities/${community.id}`);
  }, [navigate]);

  const openClinic = useCallback((clinic) => {
    navigate(`/clinic/${clinic.id}`);
  }, [navigate]);

  // Main fetch: "all" loads the grouped overview; a category tab loads its paginated results.
  useEffect(() => {
    if (!query) {
      setAllData(null);
      setItems({ people: [], posts: [], communities: [], clinics: [] });
      setCounts({ people: 0, posts: 0, communities: 0, clinics: 0 });
      setPagination(null);
      setError("");
      return;
    }

    const seq = ++requestSeq.current;
    setIsLoading(true);
    setError("");
    setPagination(null);
    setFilteredTotal(null);

    // Keep rail counts accurate even while a submenu filter is active
    if (type !== "all") {
      searchAPI
        .globalSearch({ q: query, type: "all" })
        .then((response) => {
          if (seq !== requestSeq.current) return;
          const data = response.data?.data || {};
          setCounts(data.counts || { people: 0, posts: 0, communities: 0, clinics: 0 });
        })
        .catch(() => {});
    }

    const params = { q: query, type, page: 1, per_page: 10 };
    if (type === "people") {
      if (userType) params.user_type = userType;
      if (subType) params.sub_type = subType;
    }
    if (type === "posts" && postType) params.post_type = postType;

    searchAPI
      .globalSearch(params)
      .then((response) => {
        if (seq !== requestSeq.current) return;
        const payload = response.data;

        if (type === "all") {
          const data = payload?.data || {};
          setAllData(data);
          setCounts(data.counts || { people: 0, posts: 0, communities: 0, clinics: 0 });
          setItems({
            people: data.people?.items || [],
            posts: data.posts?.items || [],
            communities: data.communities?.items || [],
            clinics: data.clinics?.items || [],
          });
        } else {
          const list = payload?.data || [];
          setAllData(null);
          setItems((prev) => ({ ...prev, [type]: list }));
          if (filterActive) {
            setFilteredTotal(payload?.pagination?.total ?? list.length);
          } else {
            setCounts((prev) => ({
              ...prev,
              [type]: payload?.pagination?.total ?? list.length,
            }));
          }
          setPagination(payload?.pagination || null);
        }
      })
      .catch(() => {
        if (seq !== requestSeq.current) return;
        setError(tr("search.error_generic", "Something went wrong while searching. Please try again."));
      })
      .finally(() => {
        if (seq !== requestSeq.current) return;
        setIsLoading(false);
      });
  }, [query, type, userType, subType, postType, tr, filterActive]);

  const loadMore = () => {
    if (!pagination?.has_more_pages || isLoadingMore) return;

    const seq = requestSeq.current;
    setIsLoadingMore(true);

    const params = { q: query, type, page: pagination.current_page + 1, per_page: pagination.per_page || 10 };
    if (type === "people") {
      if (userType) params.user_type = userType;
      if (subType) params.sub_type = subType;
    }
    if (type === "posts" && postType) params.post_type = postType;

    searchAPI
      .globalSearch(params)
      .then((response) => {
        if (seq !== requestSeq.current) return;
        const payload = response.data;
        const list = payload?.data || [];
        setItems((prev) => ({ ...prev, [type]: [...(prev[type] || []), ...list] }));
        setPagination(payload?.pagination || null);
      })
      .catch(() => {
        if (seq !== requestSeq.current) return;
        setError(tr("search.error_load_more", "Could not load more results. Please try again."));
      })
      .finally(() => {
        if (seq !== requestSeq.current) return;
        setIsLoadingMore(false);
      });
  };

  const renderPreview = (key, list, renderItem) => (
    <Section
      icon={SECTION_ICONS[key]}
      title={SECTION_TITLES[key]}
      count={counts[key]}
      onSeeAll={() => switchTab(key)}
    >
      {list.slice(0, PREVIEW_LIMIT).map(renderItem)}
    </Section>
  );

  const showEmptyAll = !isLoading && allData
    && counts.people === 0 && counts.posts === 0 && counts.communities === 0 && counts.clinics === 0;

  return (
    <div
      className="search-page"
      style={{
        "--search-primary": colors.primary,
        "--search-hover": colors.hover,
        "--search-light": colors.light,
        "--search-secondary": colors.secondary,
      }}
    >
      <div className="search-page-layout">
        <aside className="search-page-rail">
          <h1 className="search-page-title">{tr("search.title", "Search results")}</h1>
          <QuerySearchBox query={query} searchParams={searchParams} setSearchParams={setSearchParams} />
          <nav className="search-tabs" aria-label="Search categories">
            {TABS.map((tab) => (
              <React.Fragment key={tab.key}>
                <button
                  type="button"
                  className={`search-tab${type === tab.key ? " search-tab-active" : ""}`}
                  onClick={() => switchTab(tab.key)}
                >
                  <span className="search-tab-label">{tr(tab.labelKey, TAB_FALLBACKS[tab.key])}</span>
                  {tab.key !== "all" ? (
                    <span className="search-tab-count">{counts[tab.key] ?? 0}</span>
                  ) : null}
                </button>
                {tab.key === "people" && type === "people" ? (
                  <SubMenu
                    options={PEOPLE_SUBMENU}
                    current={userType}
                    childCurrent={subType}
                    onSelect={(value) => {
                      const params = new URLSearchParams(searchParams);
                      if (value) params.set("user_type", value);
                      else params.delete("user_type");
                      params.delete("sub_type");
                      setSearchParams(params);
                    }}
                    onSelectChild={(value) => setFilterParam("sub_type", value)}
                  />
                ) : null}
                {tab.key === "posts" && type === "posts" ? (
                  <SubMenu
                    options={POSTS_SUBMENU}
                    current={postType}
                    onSelect={(value) => setFilterParam("post_type", value)}
                    onSelectChild={(value) => setFilterParam("post_type", value)}
                  />
                ) : null}
              </React.Fragment>
            ))}
          </nav>
        </aside>

        <main className="search-page-results">
          {!query ? (
            <div className="search-empty-state">
              <SearchIcon />
              <p className="search-empty-title">{tr("search.initial_title", "Search Sulhafa")}</p>
              <p className="search-empty-subtitle">{tr("search.initial_subtitle", "Type a name, post or community in the search bar above to get started.")}</p>
            </div>
          ) : error ? (
            <div className="search-empty-state">
              <SearchIcon />
              <p className="search-empty-title">{error}</p>
            </div>
          ) : isLoading && type === "all" && !allData ? (
            <div className="search-loading" aria-label="Loading search results">
              <span className="search-spinner" />
            </div>
          ) : isLoading && type !== "all" && items[type]?.length === 0 ? (
            <div className="search-loading" aria-label="Loading search results">
              <span className="search-spinner" />
            </div>
          ) : showEmptyAll ? (
            <EmptyState query={query} tkey="search.empty_title" fallback='No results for "{query}"' />
          ) : type === "all" && allData ? (
            <div className="search-sections">
              {counts.people > 0 ? renderPreview("people", items.people, (person) => (
                <PeopleRow key={person.user_id || person.id} person={person} onOpen={openPerson} />
              )) : null}
              {counts.posts > 0 ? renderPreview("posts", items.posts, (post) => (
                <PostRow key={post.id} post={post} onOpen={openPost} />
              )) : null}
              {counts.communities > 0 ? renderPreview("communities", items.communities, (community) => (
                <CommunityRow key={community.id} community={community} onOpen={openCommunity} />
              )) : null}
              {counts.clinics > 0 ? renderPreview("clinics", items.clinics, (clinic) => (
                <ClinicRow key={clinic.id} clinic={clinic} onOpen={openClinic} />
              )) : null}
            </div>
          ) : type === "people" ? (
            <div className="search-sections">
              <Section icon={<PeopleIcon />} title={SECTION_TITLES.people} count={filteredTotal ?? counts.people}>
                {(items.people || []).map((person) => (
                  <PeopleRow key={person.user_id || person.id} person={person} onOpen={openPerson} />
                ))}
                {!isLoading && (items.people || []).length === 0 ? <EmptyState query={query} tkey="search.empty_people" fallback='No people results for "{query}"' /> : null}
              </Section>
              {pagination?.has_more_pages ? (
                <button type="button" className="search-load-more" onClick={loadMore} disabled={isLoadingMore}>
                  {isLoadingMore ? tr("search.loading_more", "Loading...") : tr("search.load_more", "Load more")}
                </button>
              ) : null}
            </div>
          ) : type === "posts" ? (
            <div className="search-sections">
              <Section icon={<PostsIcon />} title={SECTION_TITLES.posts} count={filteredTotal ?? counts.posts}>
                {(items.posts || []).map((post) => (
                  <PostRow key={post.id} post={post} onOpen={openPost} />
                ))}
                {!isLoading && (items.posts || []).length === 0 ? <EmptyState query={query} tkey="search.empty_post" fallback='No post results for "{query}"' /> : null}
              </Section>
              {pagination?.has_more_pages ? (
                <button type="button" className="search-load-more" onClick={loadMore} disabled={isLoadingMore}>
                  {isLoadingMore ? tr("search.loading_more", "Loading...") : tr("search.load_more", "Load more")}
                </button>
              ) : null}
            </div>
          ) : type === "communities" ? (
            <div className="search-sections">
              <Section icon={<CommunitiesIcon />} title={SECTION_TITLES.communities} count={filteredTotal ?? counts.communities}>
                {(items.communities || []).map((community) => (
                  <CommunityRow key={community.id} community={community} onOpen={openCommunity} />
                ))}
                {!isLoading && (items.communities || []).length === 0 ? <EmptyState query={query} tkey="search.empty_community" fallback='No community results for "{query}"' /> : null}
              </Section>
              {pagination?.has_more_pages ? (
                <button type="button" className="search-load-more" onClick={loadMore} disabled={isLoadingMore}>
                  {isLoadingMore ? tr("search.loading_more", "Loading...") : tr("search.load_more", "Load more")}
                </button>
              ) : null}
            </div>
          ) : type === "clinics" ? (
            <div className="search-sections">
              <Section icon={<ClinicsIcon />} title={SECTION_TITLES.clinics} count={filteredTotal ?? counts.clinics}>
                {(items.clinics || []).map((clinic) => (
                  <ClinicRow key={clinic.id} clinic={clinic} onOpen={openClinic} />
                ))}
                {!isLoading && (items.clinics || []).length === 0 ? <EmptyState query={query} tkey="search.empty_clinic" fallback='No clinic results for "{query}"' /> : null}
              </Section>
              {pagination?.has_more_pages ? (
                <button type="button" className="search-load-more" onClick={loadMore} disabled={isLoadingMore}>
                  {isLoadingMore ? tr("search.loading_more", "Loading...") : tr("search.load_more", "Load more")}
                </button>
              ) : null}
            </div>
          ) : null}
        </main>
      </div>
    </div>
  );
};

export default SearchPage;

